import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HeaderComponent } from '../../shared/components/header/header.component';
import { ConsumerSidebarComponent } from '../sidebar/consumer-sidebar.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import { MyProjectComponent } from '../../shared/components/my-project/my-project.component';
import { ModalComponent } from '../../shared/components/primeng/modal/modal.component';
import { CanComponentDeactivate } from '../../guards/my-projects-deactivate.guard';
import { AuthApiService } from '@angular-workspace/services';
import { GoogleAnalyticsService } from '../../services/google-analytics.service';
import { Observable } from 'rxjs';

/** Access to My Projects: only allowed when Compute is in active roles (not just pending). */
export type MyProjectsAccessLevel = 'loading' | 'none' | 'pending' | 'full';

@Component({
  selector: 'app-consumer-my-projects',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HeaderComponent,
    ConsumerSidebarComponent,
    PageHeaderComponent,
    MyProjectComponent,
    ModalComponent,
  ],
  templateUrl: './my-projects.component.html',
  styleUrl: './my-projects.component.scss',
})
export class ConsumerMyProjectsComponent implements CanComponentDeactivate {
  @ViewChild(MyProjectComponent) myProjectComponent!: MyProjectComponent;
  private readonly ACCESS_MODAL_SUPPRESS_KEY = 'nha_my_projects_access_modal_suppressed';

  /** Compute role check: full = can use page; pending = show pending message; none = show no-access message. */
  accessLevel: MyProjectsAccessLevel = 'loading';

  /** When true, the access modal was closed via "Role Upgrade" (so we don't show it again until next visit). */
  accessModalDismissed = false;
  /** Checkbox state in modal: persist preference to stop showing this modal again. */
  doNotShowAccessModalAgain = false;
  private isAccessModalSuppressed = false;

  /** Show modal when access is none or pending and user hasn't clicked Role Upgrade. */
  get showAccessModal(): boolean {
    return (this.accessLevel === 'none' || this.accessLevel === 'pending') &&
      !this.accessModalDismissed &&
      !this.isAccessModalSuppressed;
  }

  constructor(
    private authApiService: AuthApiService,
    private router: Router,
    private googleAnalytics: GoogleAnalyticsService
  ) {}

  async ngOnInit(): Promise<void> {
    this.loadAccessModalPreference();
    await this.checkComputeAccess();
    if ((this.accessLevel === 'none' || this.accessLevel === 'pending') && this.isAccessModalSuppressed) {
      this.router.navigate(['/consumer/home']);
      return;
    }
    if (this.accessLevel === 'full') {
      this.googleAnalytics.trackMyProjectView();
    }
  }

  private loadAccessModalPreference(): void {
    try {
      if (typeof window === 'undefined') return;
      this.isAccessModalSuppressed = localStorage.getItem(this.ACCESS_MODAL_SUPPRESS_KEY) === 'true';
      this.doNotShowAccessModalAgain = this.isAccessModalSuppressed;
    } catch {
      this.isAccessModalSuppressed = false;
      this.doNotShowAccessModalAgain = false;
    }
  }

  private persistAccessModalPreference(): void {
    try {
      if (typeof window === 'undefined') return;
      if (this.doNotShowAccessModalAgain) {
        localStorage.setItem(this.ACCESS_MODAL_SUPPRESS_KEY, 'true');
        this.isAccessModalSuppressed = true;
      } else {
        localStorage.removeItem(this.ACCESS_MODAL_SUPPRESS_KEY);
        this.isAccessModalSuppressed = false;
      }
    } catch {
      // no-op
    }
  }

  /**
   * Check if user has Compute in active roles (allow) or only in pending (show pending) or neither (no access).
   */
  private async checkComputeAccess(): Promise<void> {
    try {
      const userData = await this.authApiService.getUserInfo();
      const actual = userData?.['result'] ?? userData;
      const roles: string[] = actual?.roles ?? [];
      const pendingRoles: string[] = actual?.pendingRoles ?? [];

      const hasActiveCompute = roles.some((r: string) => r.toLowerCase() === 'compute');
      const hasPendingCompute = pendingRoles.some((r: string) => r.toLowerCase() === 'compute');

      if (hasActiveCompute) {
        this.accessLevel = 'full';
      } else if (hasPendingCompute) {
        this.accessLevel = 'pending';
      } else {
        this.accessLevel = 'none';
      }
    } catch (error) {
      console.error('Error checking Compute role for My Projects:', error);
      this.accessLevel = 'none';
    }
  }

  onAccessModalClose(): void {
    this.persistAccessModalPreference();
    this.router.navigate(['/consumer/home']);
  }

  /** Close this modal and open the header's Role Upgrade modal. */
  onRoleUpgradeClick(): void {
    this.persistAccessModalPreference();
    this.accessModalDismissed = true;
    const event = new CustomEvent('openUpgradeModal', { detail: { source: 'my-projects' } });
    window.dispatchEvent(event);
  }

  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (this.myProjectComponent) {
      return this.myProjectComponent.canDeactivate();
    }
    return true;
  }
}
